
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin as observableForkJoin, Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { MarineGroup } from '../classes/marine-group';

export interface WorkoutSessionQuestionSurveyResponseData {
  aggregation: { [key: number]: number };
  text: string;
  allowed_answer_type: string;
  allowed_answer_value_type: string;
}

export interface WorkoutSessionSurveyInstanceData {
  uuid: string;
  survery_uuid: string;
  date_completed: string;
  date_sent: string;
  is_active: boolean;
  is_completed: boolean;
  user: {
    uuid: string;
    firstname: string;
    lastname: string;
    rank: string;
  };
}

export interface WorkoutSessionSurveyResponseData {
  date_time: string;
  questions: { [key: string]: WorkoutSessionQuestionSurveyResponseData };
  survey_instances: WorkoutSessionSurveyInstanceData[];
}

@Injectable()
export class GroupsRestService {

  constructor(private http: HttpClient,
              private snackbar: MatSnackBar) { }

  handleError(e) {
    this.snackbar.open('Error: ' + e.message, 'Dismiss');
    return observableThrowError(e.message || 'Server error');
  }

  getGroups() {
    return this.get('/Group/?onlyOwned=true').pipe(
      /*.flatMap((res) => {
        let obs = [];

        res.forEach(group => {
          obs.push(this.getPlansInGroup(group));
        });

        return Observable.forkJoin(obs);
      })*/
      catchError(e => this.handleError(e)));
  }

  getGroupByUuid(uuid: string) {
    return this.get('/Group/' + uuid).pipe(
      catchError(e => this.handleError(e)));
  }

  getAllUsers() {
    return this.get('/User/').pipe(
      catchError(e => this.handleError(e)));
  }

  createGroup(group: MarineGroup) {
    return this.post('/Group/', {
      name: group.name,
      description: '',
      'private': group.privateGroup
    }).pipe(
      catchError(e => this.handleError(e)));
  }

  updateGroup(group: MarineGroup) {
    return this.put('/Group/' + group.uuid, {
      name: group.name,
      description: '',
      'private': group.privateGroup
    }).pipe(
      catchError(e => this.handleError(e)));
  }

  publishToGroups(planId: string, groupIDs: string[]) {
    const postObs = [];

    groupIDs.forEach(groupId => {
      postObs.push(this.post('/Group/publishPlan', {
        planUUID: planId,
        groupUUID: groupId
      }));
    });

    return observableForkJoin(postObs);
  }

  getPlansInGroup(group: any) {
    return this.get('/getPlansInGroup/' + group.uuid).pipe(
      map((res) => {
        res['groupName'] = group.name;
        return res;
      }),
      catchError(e => this.handleError(e)));
  }

  removeMarine(groupUuid: string, memberUuid: string) {
    return this.delete('/Group/' + groupUuid + '/members/' + memberUuid);
  }

  removeOwner(groupUuid: string, ownerUuid: string) {
    return this.delete('/Group/' + groupUuid + '/owners/' + ownerUuid);
  }

  inviteMarines(groupUuid: string, emails: string[]) {
    return this.post('/Group/' + groupUuid + '/invite', {emails: emails});
  }

  addOwner(groupUuid: string, userUuid: string) {
    return this.post('/Group/' + groupUuid + '/owners/', {uuid: userUuid});
  }

  addOwners(groupUuid: string, users: string[]) {
    const obs = [];

    users.forEach(uuid => {
      obs.push(this.addOwner(groupUuid, uuid));
    });

    return observableForkJoin(obs);
  }

  getGroupStats(groupUuid: string) {
    return this.get('/Statistics/getGroupStatistics/' + groupUuid).pipe(
      map((res) => res['workouts']));
  }

  getGroupSurveryResponses(groupId: string): Observable<WorkoutSessionSurveyResponseData[]> {
    return this.get(`/Group/${groupId}/survey_responses/`).pipe(
      map((res) => res['workout_sessions']));
  }

  getSurveyInstanceData(surveyInstanceId: string) {
    return this.get(`/Mobile/survey/${surveyInstanceId}`);
  }

  private get (route: string) {
    return this.http.get(this.buildRoute(route));
  }

  private post (route: string, data: any) {
    return this.http.post(this.buildRoute(route), data);
  }

  private buildRoute (route: string) {
    return environment.applySubstitutions(environment.api_url) + 'api' + route;
  }

  private put(route: string, data: any) {
    return this.http.put(this.buildRoute(route), data);
  }

  private delete(route: string) {
    return this.http.delete(this.buildRoute(route));
  }
}
